import { useDataTable } from '@/components/data-table/data-table-provider'
import { useFilterApply } from '@/components/data-table/data-table-store-sync'
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from '@/components/ui/input-group'
import { Label } from '@/components/ui/label'
import { Search } from 'lucide-react'
import { useState } from 'react'
import type { DataTableInputFilterField } from './types'

function getFilter(filterValue: unknown) {
  return typeof filterValue === 'string' ? filterValue : null
}

export function DataTableFilterInput<TData>({
  value: _value,
}: DataTableInputFilterField<TData>) {
  const value = _value as string
  const { table, columnFilters } = useDataTable()
  const { applyFilters } = useFilterApply()
  const column = table.getColumn(value)
  const filterValue = columnFilters.find((i) => i.id === value)?.value
  const filters = getFilter(filterValue)
  const [input, setInput] = useState<string | null>(filters)

  // Sync external filter changes (e.g. reset) back to local input
  const [prevFilters, setPrevFilters] = useState(filters)
  if (prevFilters !== filters) {
    setPrevFilters(filters)
    if (input?.trim() !== filters) {
      setInput(filters)
    }
  }

  return (
    <div className="grid w-full gap-1.5">
      <Label htmlFor={value} className="text-muted-foreground sr-only px-2">
        {value}
      </Label>
      <InputGroup className="border-neutral-subtle-border bg-app h-10 rounded-lg shadow-none">
        <InputGroupAddon>
          <Search className="text-neutral-low-contrast mt-0.5 h-4 w-4" />
        </InputGroupAddon>
        <InputGroupInput
          placeholder="Search"
          name={value}
          id={value}
          value={input || ''}
          onChange={(e) => {
            const val = e.target.value
            setInput(val)
            // Update the draft only; the query runs when the Search button is
            // clicked or Enter is pressed, not on keystroke.
            column?.setFilterValue(val.trim() === '' ? null : val)
          }}
          onKeyDown={(e) => {
            // Enter applies the whole draft, same as clicking Search.
            if (e.key === 'Enter') {
              e.preventDefault()
              applyFilters()
            }
          }}
        />
      </InputGroup>
    </div>
  )
}
